import { StatItem, DiveSectorCard, ClientLogo, LocationPinItem } from "@/types";

export const homePageData = {
  hero: {
    bgImage: "/images/home/hero.png",
    headingLine1: "Enhanced underwater capability through superior equipment, with world-class training, building self-sufficiency that is second to none.",
    scrollAriaLabel: "Scroll to content",
  },
  about: {
    title: "Service Record",
    subtitle:
      "No outreach, no campaigns, no pitch decks – just a decade of working with India’s Military, Paramilitary & Rescue Forces that speaks for itself.",
    stats: [
      { value: "8+", label: "Years serving Armed Forces" },
      { value: "300+", label: "Pros trained" },
      { value: "20+", label: "Brands represented" },
      { value: "375+", label: "HADR/Combat kits deployed" },
    ] as StatItem[],
  },
  focusSectors: {
    title: "Domains of Deployment",
    subtitle: "Different uniforms, different missions, one strict non-negotiable – In-water readiness before the mission demands it!",
    bgVideo: "/video/focus-sectors.mp4",
    cards: [
      {
        title: "Navy & Coast Guard",
        description:
          "From Aircraft Carriers and Patrol Boats to Specialised Diving Equipment – outfitting Navy & Coast Guard Divers to operate in the most demanding conditions with confidence and ease.",
        image: "/images/home/domain-of-deployment/Coast-Guard.jpeg",
        variant: "navy",
      },
      {
        title: "Public Safety & HADR",
        description:
          "Equipping our courageous divers with humanitarian and disaster relief gear to aid them in saving lives.",
        image: "/images/home/domain-of-deployment/Public_Safety.jpeg",
        variant: "safety",
      },
      {
        title: "Civilian Training",
        description:
          "Introducing the country to its own waters, in a safe way – one day, one diver, one skill at a time.",
        image: "/images/home/domain-of-deployment/civilian_training.png",
        variant: "civilian",
      },
      {
        title: "Combat & Special Ops Diving",
        description:
          "Covert. Precise. Silent. Adaptable. Mobile – as your missions demand, as our equipment delivers.",
        image: "/images/home/domain-of-deployment/ops-diving.jpeg",
        variant: "combat",
      },
    ] as DiveSectorCard[],
  },
  clientsServed: {
    title: "Esteemed Partners",
    subtitle:
      "Trust isn’t built on testimonials. It’s earned by serving esteemed partners whose faith made us their preferred vendors.",
    tagline:
      "Word of mouth that built the reputation. Work ethic that reflected global standards.",
    logos: [
      { id: 18, src: "/images/home/client-served/ASDMA.png", alt: "ASDMA", name: "ASDMA" },
      { id: 14, src: "/images/home/client-served/Army Adventure Wing.jpg", alt: "Army Adventure Wing", name: "Army Adventure Wing" },
      { id: 13, src: "/images/home/client-served/Eastern Command.png", alt: "Eastern Command", name: "Eastern Command" },
      { id: 16, src: "/images/home/client-served/Goa Shipyard Ltd..jpeg", alt: "Goa Shipyard Ltd.", name: "Goa Shipyard Ltd." },
      { id: 8, src: "/images/home/client-served/Indian Navy.png", alt: "Indian Navy", name: "Indian Navy" },
      { id: 7, src: "/images/home/client-served/Indian Army.png", alt: "Indian Army", name: "Indian Army" },
      { id: 9, src: "/images/home/client-served/Indian Coast Guard.png", alt: "Indian Coast Guard", name: "Indian Coast Guard" },
      { id: 15, src: "/images/home/client-served/INS Vikrant (IAC-71).png", alt: "INS Vikrant (IAC-71)", name: "INS Vikrant (IAC-71)" },
      { id: 19, src: "/images/home/client-served/Kerala Fire and Rescue Services.png", alt: "Kerala Fire and Rescue Services", name: "Kerala Fire and Rescue Services" },
      { id: 10, src: "/images/home/client-served/MARCOS.png", alt: "MARCOS", name: "MARCOS" },
      { id: 17, src: "/images/home/client-served/NDRF.png", alt: "NDRF", name: "NDRF" },
      { id: 3, src: "/images/home/client-served/10 Para SF.png", alt: "10 Para SF", name: "10 Para SF" },
      { id: 4, src: "/images/home/client-served/11 Para SF.png", alt: "11 Para SF", name: "11 Para SF" },
      { id: 5, src: "/images/home/client-served/12 Para SF.png", alt: "12 Para SF", name: "12 Para SF" },
      { id: 1, src: "/images/home/client-served/1 Para SF.png", alt: "1 Para SF", name: "1 Para SF" },
      { id: 6, src: "/images/home/client-served/21 Para SF.png", alt: "21 Para SF", name: "21 Para SF" },
      { id: 2, src: "/images/home/client-served/3 Para SF.png", alt: "3 Para SF", name: "3 Para SF" },
      { id: 12, src: "/images/home/client-served/Special Forces Training School.png", alt: "Special Forces Training School", name: "Special Forces Training School" },
      { id: 20, src: "/images/home/client-served/SDRF Maharashtra.png", alt: "SDRF Maharashtra", name: "SDRF Maharashtra" },
      { id: 21, src: "/images/home/client-served/SDRF Uttar Pradesh.png", alt: "SDRF Uttar Pradesh", name: "SDRF Uttar Pradesh" },
      { id: 22, src: "/images/home/client-served/SDRF Uttarakhand.png", alt: "SDRF Uttarakhand", name: "SDRF Uttarakhand" },
      { id: 23, src: "/images/home/client-served/Uttar Pradesh Police.png", alt: "Uttar Pradesh Police", name: "Uttar Pradesh Police" },
    ] as ClientLogo[],
  },

  locations: {
    title: "Our Locations",
    subtitle: "Service Bench",
    description:
      "We are equipped with in-house, certified technicians and instructors to service the equipment we supply across three different locations - Mumbai, Goa & Havelock Island.",
    cta: { label: "View All", href: "/contact#service-locations" },
    mapLabel: "Dotted map of India showing Purple Octopus service bench locations",
    pins: [
      { id: "mumbai", name: "Mumbai", lat: 19.09218212851549, lng: 73.02835639944688 },
      { id: "goa", name: "Goa", lat: 15.530427280814134, lng: 73.76678611721434 },
      { id: "havelock-island", name: "Havelock Island", lat: 12.02873883847458, lng: 93.00288081534205 },
    ] satisfies LocationPinItem[],
  },

  authorizedDealers: {
    title: "Brands We Represent",
    subtitle:
      "Global brands with world class equipment - all under one roof.",
    logos: [
      { id: 18, src: "/images/home/distributors/Al-can Cylinders.png", alt: "Al-can Cylinders", type: "distributor" },
      { id: 1, src: "/images/home/exclusive-distributor/Apeks.png", alt: "Apeks", type: "exclusive-distributor" },
      { id: 3, src: "/images/home/distributors/Aqualung Military and Professional.png", alt: "Aqualung Military and Professional", type: "distributor" },
      { id: 2, src: "/images/home/exclusive-distributor/Aqualung.png", alt: "Aqualung", type: "exclusive-distributor" },
      { id: 10, src: "/images/home/authorized-dealer/Aropec.png", alt: "Aropec", type: "dealer" },
      { id: 17, src: "/images/home/distributors/BARE.png", alt: "BARE", type: "distributor" },
      { id: 11, src: "/images/home/authorized-dealer/Coltri Compressors.png", alt: "Coltri Compressors", type: "dealer" },
      { id: 16, src: "/images/home/distributors/DiveSoft.png", alt: "DiveSoft", type: "distributor" },
      { id: 19, src: "/images/home/distributors/feelfree.png", alt: "feelfree", type: "distributor" },
      { id: 12, src: "/images/home/authorized-dealer/Klepper Kayaks.png", alt: "Klepper Kayaks", type: "dealer" },
      { id: 4, src: "/images/home/exclusive-distributor/Mares.png", alt: "Mares", type: "exclusive-distributor" },
      { id: 21, src: "/images/home/distributors/NRC International.png", alt: "NRC International", type: "distributor" },
      { id: 13, src: "/images/home/authorized-dealer/Ocean Reef.png", alt: "Ocean Reef", type: "dealer" },
      { id: 5, src: "/images/home/exclusive-distributor/Orcatorch.png", alt: "Orcatorch", type: "exclusive-distributor" },
      { id: 9, src: "/images/home/exclusive-distributor/rEvo.png", alt: "rEvo", type: "exclusive-distributor" },
      { id: 20, src: "/images/home/distributors/SOS Group.png", alt: "SOS Group", type: "distributor" },
      { id: 14, src: "/images/home/authorized-dealer/San-o-Sub.png", alt: "San-o-Sub", type: "dealer" },
      { id: 6, src: "/images/home/exclusive-distributor/Seacraft.png", alt: "Seacraft", type: "exclusive-distributor" },
      { id: 7, src: "/images/home/exclusive-distributor/Shearwater Research.png", alt: "Shearwater Research", type: "exclusive-distributor" },
      { id: 8, src: "/images/home/exclusive-distributor/Tusa.png", alt: "Tusa", type: "exclusive-distributor" },
      { id: 15, src: "/images/home/authorized-dealer/Watershed.png", alt: "Watershed", type: "dealer" },
    ] as ClientLogo[],
  },
};
